/**
 * ElecBid — the "what is the AI doing right now" trail, shared by the
 * assessment's Scope Chat and the quote editor's Refine chat.
 *
 * Both chats already streamed a one-line status ("📖 checking the price book…")
 * that vanished the moment each step finished — so a multi-tool turn read as a
 * flicker, and once the reply landed there was no trace of what the AI actually
 * did. This renders the steps Claude-Code-style: each tool call is a line that
 * shows WHAT it was asked (the search query, the rule being saved — the server's
 * `tool_call` event) and what came back ("6 matches", "saved"), stays visible
 * while the turn streams, and collapses to a "⚙ N steps" toggle afterwards.
 *
 * The step array lives on the chat message (like `thinking`); the fold helpers
 * below are pure so callers can `steps = startStep(steps, …)` and patch the
 * message the same way both chats already patch streaming text.
 */
import React, { useState } from "react";

/** A tool started — append a running step. */
export function startStep(steps, name, label) {
  return [...steps, { name, label: String(label || name || "working…"), arg: "", done: false, ok: true, note: "" }];
}

/** The tool's input finished streaming — attach what it was asked to the newest matching step. */
export function argStep(steps, name, arg) {
  const a = String(arg || "").trim();
  if (!a) return steps;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].name === name && !steps[i].done && !steps[i].arg) {
      const next = steps.slice();
      next[i] = { ...next[i], arg: a };
      return next;
    }
  }
  return steps;
}

/** The tool finished — close the newest matching running step. */
export function finishStep(steps, name, ok, note) {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].name === name && !steps[i].done) {
      const next = steps.slice();
      next[i] = { ...next[i], done: true, ok: ok !== false, note: String(note || "") };
      return next;
    }
  }
  return steps;
}

/** One-line outcome for a finished tool, from the server's tool_result detail. */
export function stepNote(name, detail, ok) {
  const d = detail || {};
  if (ok === false) return String(d.error || "failed").slice(0, 90);
  if (name === "pricebook_search") {
    return Array.isArray(d.results) ? `${d.results.length} match${d.results.length === 1 ? "" : "es"}` : "done";
  }
  if (name === "remember_this") return d.saved ? "saved" : "not saved";
  if (name === "recall_memories") return typeof d.count === "number" ? `${d.count} rule${d.count === 1 ? "" : "s"}` : "done";
  if (name === "forget_memory") return "retired";
  if (name === "count_devices_on_plan") {
    return d.counts_unavailable ? "couldn't read this set's legend" : `counted ${d.source || "the print"}`;
  }
  if (name === "view_job_photos") {
    return typeof d.shown === "number" ? `read ${d.shown} of ${d.total} photo${d.total === 1 ? "" : "s"}` : "done";
  }
  // Generic reads (refine research tools etc.) — surface a result count if one rode along.
  for (const k of ["results", "jobs", "rows", "items", "matches"]) {
    if (Array.isArray(d[k])) return `${d[k].length} result${d[k].length === 1 ? "" : "s"}`;
  }
  return "done";
}

/**
 * The trail itself. While streaming every step is visible live (running steps
 * end in "…", finished ones in ✓/⚠ + outcome); once the turn settles it
 * collapses to a toggle so old turns don't bury the conversation.
 */
export function ActivityTrail({ steps, streaming, colors }) {
  const [open, setOpen] = useState(false);
  const c = { muted: "#8892a4", border: "#2a3140", ...(colors || {}) };
  if (!Array.isArray(steps) || !steps.length) return null;

  const lines = (
    <div style={{ borderLeft: `2px solid ${c.border}`, paddingLeft: 8, margin: "2px 0 6px", display: "flex", flexDirection: "column", gap: 2 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ fontSize: 11, color: c.muted, lineHeight: 1.45 }}>
          {s.label.replace(/…\s*$/, "")}
          {s.arg ? <span style={{ fontStyle: "italic" }}> “{s.arg}”</span> : null}
          {s.done ? (s.ok ? ` ✓ ${s.note || "done"}` : ` ⚠ ${s.note || "failed"}`) : " …"}
        </div>
      ))}
    </div>
  );

  if (streaming) return lines;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ display: "block", background: "none", border: "none", color: c.muted, fontStyle: "italic", fontSize: 11, cursor: "pointer", padding: 0, marginBottom: 4 }}
      >⚙ {steps.length} step{steps.length === 1 ? "" : "s"} {open ? "▾" : "▸"}</button>
      {open && lines}
    </>
  );
}
